import { ViewQueueTypes } from './types';
import apiClient from '../../utils/apiConfig';
import { normalizeVideoData } from '../../utils/dataHelpers';

// Basic action creators
export const setSelectedQueue = (queue) => ({
  type: ViewQueueTypes.SET_SELECTED_QUEUE,
  payload: queue
});

export const clearSelectedQueue = () => ({
  type: ViewQueueTypes.CLEAR_SELECTED_QUEUE
});

export const updateQueueFilters = (filters) => ({
  type: ViewQueueTypes.UPDATE_QUEUE_FILTERS,
  payload: filters
});

export const setQueueViewMode = (mode) => ({
  type: ViewQueueTypes.SET_QUEUE_VIEW_MODE,
  payload: mode
});

export const setActiveTab = (tab) => ({
  type: ViewQueueTypes.SET_ACTIVE_TAB,
  payload: tab
});

/**
 * Toggles the create queue modal visibility
 * @param {boolean|object} isOpen - If boolean, sets modal visibility. If object, can include {isOpen, clearEditState} properties
 * @returns {object} Redux action
 */
export const toggleCreateModal = (isOpen) => ({
  type: ViewQueueTypes.TOGGLE_CREATE_MODAL,
  // Support both boolean (backward compatibility) and object with additional options
  payload: typeof isOpen === 'object' ? isOpen : { isOpen, clearEditState: true }
});

export const toggleEditModal = (isOpen, queue = null) => ({
  type: ViewQueueTypes.TOGGLE_EDIT_MODAL,
  payload: { isOpen, queue }
});

// Thunk action for fetching user queues
export const fetchUserQueues = () => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.FETCH_USER_QUEUES_REQUEST });
  
  try {
    console.log('[ViewQueueService] Fetching user queues');
    console.time('queue-fetch-time');
    
    const response = await apiClient.get('/api/view-queues/user');
    
    console.timeEnd('queue-fetch-time');
    console.log(`Fetched ${response.data.userQueues.length} queues`);
    
    dispatch({
      type: ViewQueueTypes.FETCH_USER_QUEUES_SUCCESS,
      payload: response.data.userQueues || []
    });
    
    return response.data.userQueues;
  } catch (error) {
    console.error('Failed to fetch user queues:', error);
    
    dispatch({
      type: ViewQueueTypes.FETCH_USER_QUEUES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to fetch queues'
    });
    
    return [];
  }
};

// Thunk action for creating a new queue
export const createQueue = (queueData) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.CREATE_QUEUE_REQUEST });
  
  try {
    const response = await apiClient.post('/api/view-queues/create', queueData);
    
    dispatch({
      type: ViewQueueTypes.CREATE_QUEUE_SUCCESS,
      payload: response.data.queue
    });
    
    // Refresh queues after creating
    dispatch(fetchUserQueues());
    
    return { success: true, queue: response.data.queue };
  } catch (error) {
    console.error('Failed to create queue:', error);
    
    dispatch({
      type: ViewQueueTypes.CREATE_QUEUE_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to create queue'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for updating a queue
export const updateQueue = (queueId, updates) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.UPDATE_QUEUE_REQUEST });
  
  try {
    const response = await apiClient.put(`/api/view-queues/${queueId}`, updates);
    
    dispatch({
      type: ViewQueueTypes.UPDATE_QUEUE_SUCCESS,
      payload: response.data.queue
    });
    
    // Also update the selected queue if it's the one being edited
    dispatch(setSelectedQueue(response.data.queue));
    
    return { success: true, queue: response.data.queue };
  } catch (error) {
    console.error(`Failed to update queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.UPDATE_QUEUE_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to update queue'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for deleting a queue
export const deleteQueue = (queueId) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.DELETE_QUEUE_REQUEST });
  
  try {
    await apiClient.delete(`/api/view-queues/${queueId}`);
    
    dispatch({
      type: ViewQueueTypes.DELETE_QUEUE_SUCCESS,
      payload: queueId
    });
    
    // Clear selected queue if it's the one being deleted
    dispatch(clearSelectedQueue());
    
    // Refresh queues after deleting
    dispatch(fetchUserQueues());
    
    return { success: true };
  } catch (error) {
    console.error(`Failed to delete queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.DELETE_QUEUE_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to delete queue'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for adding a video to a queue
export const addVideoToQueue = (queueId, videoData) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.ADD_VIDEO_TO_QUEUE_REQUEST });
  
  try {
    // Normalize video data to ensure consistent properties
    const normalizedVideoData = normalizeVideoData(videoData);
    
    // Optimistic update - add the video immediately
    dispatch({
      type: ViewQueueTypes.ADD_VIDEO_TO_QUEUE_SUCCESS,
      payload: { queueId, video: { ...normalizedVideoData, position: -1 } } // Temporary position
    });
    
    const response = await apiClient.post(`/api/view-queues/${queueId}/videos`, videoData);
    
    // If the API call succeeds, we'll have the correct queue state with proper positions
    dispatch(setSelectedQueue(response.data.queue));
    
    return { success: true, queue: response.data.queue };
  } catch (error) {
    console.error(`Failed to add video to queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.ADD_VIDEO_TO_QUEUE_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message || 'Failed to add video to queue',
        queueId,
        videoId: videoData.videoId
      }
    });
    
    // Refresh queues to revert the optimistic update
    dispatch(fetchUserQueues());
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for removing a video from a queue
export const removeVideoFromQueue = (queueId, videoId) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_REQUEST });
  
  try {
    // Optimistic update - remove the video immediately
    dispatch({
      type: ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_SUCCESS,
      payload: { queueId, videoId }
    });
    
    const response = await apiClient.delete(`/api/view-queues/${queueId}/videos/${videoId}`);
    
    // Update selected queue with the response
    dispatch(setSelectedQueue(response.data.queue));
    
    return { success: true, queue: response.data.queue };
  } catch (error) {
    console.error(`Failed to remove video ${videoId} from queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message || 'Failed to remove video from queue',
        queueId,
        videoId
      }
    });
    
    // Refresh queues to revert the optimistic update
    dispatch(fetchUserQueues());
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for reordering videos in a queue
export const reorderQueueVideos = (queueId, videoPositions) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.REORDER_QUEUE_VIDEOS_REQUEST });
  
  try {
    // Optimistic update - reorder the videos immediately
    dispatch({
      type: ViewQueueTypes.REORDER_QUEUE_VIDEOS_SUCCESS,
      payload: { queueId, videoPositions }
    });
    
    const response = await apiClient.put(`/api/view-queues/${queueId}/reorder`, { videoPositions });
    
    // Update selected queue with the response
    dispatch(setSelectedQueue(response.data.queue));
    
    return { success: true, queue: response.data.queue };
  } catch (error) {
    console.error(`Failed to reorder videos in queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.REORDER_QUEUE_VIDEOS_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message || 'Failed to reorder videos',
        queueId
      }
    });
    
    // Refresh queues to revert the optimistic update
    dispatch(fetchUserQueues());
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for searching public queues
export const searchQueues = (params) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.SEARCH_QUEUES_REQUEST });
  
  try {
    console.time('queue-search-time');
    
    const response = await apiClient.get('/api/view-queues/search', { params });
    
    console.timeEnd('queue-search-time');
    console.log(`Search found ${response.data.queues.length} public queues`);
    
    dispatch({
      type: ViewQueueTypes.SEARCH_QUEUES_SUCCESS,
      payload: {
        queues: response.data.queues || [],
        totalResults: response.data.totalResults || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      }
    });
    
    return { 
      success: true, 
      queues: response.data.queues || [],
      totalResults: response.data.totalResults || 0
    };
  } catch (error) {
    console.error('Failed to search queues:', error);
    
    dispatch({
      type: ViewQueueTypes.SEARCH_QUEUES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to search queues'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Thunk action for fetching a single public queue by ID (guest-safe)
export const fetchPublicQueue = (queueId) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.FETCH_PUBLIC_QUEUE_REQUEST });
  
  try {
    console.log(`[ViewQueueService] Fetching public queue: ${queueId}`);
    console.time('public-queue-fetch-time');
    
    const response = await apiClient.get(`/api/view-queues/public/${queueId}`);
    
    console.timeEnd('public-queue-fetch-time');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to load public queue');
    }
    
    console.log(`[ViewQueueService] Public queue loaded: "${response.data.queue.viewQueueName}" (${response.data.queue.videos?.length || 0} videos)`);
    
    dispatch({
      type: ViewQueueTypes.FETCH_PUBLIC_QUEUE_SUCCESS,
      payload: {
        queue: response.data.queue,
        owner: response.data.owner,
        isOwner: response.data.isOwner
      }
    });
    
    return { 
      success: true, 
      queue: response.data.queue, 
      owner: response.data.owner,
      isOwner: response.data.isOwner
    };
  } catch (error) {
    console.error(`[ViewQueueService] Failed to fetch public queue ${queueId}:`, error);
    
    dispatch({
      type: ViewQueueTypes.FETCH_PUBLIC_QUEUE_FAILURE,
      payload: error.response?.data?.message || error.message || 'Queue not found or is private'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Add these action creators to your ViewQueue actions

// Thunk action for loading a queue for playback
export const loadQueueForPlayback = (queueId, startVideoId = null) => async (dispatch) => {
  dispatch({ type: ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_REQUEST });
  
  try {
    console.time('queue-playback-load-time');
    console.log(`[ViewQueueService] Loading queue ${queueId} for playback, startVideoId: ${startVideoId}`);
    
    // Check authentication status
    const token = localStorage.getItem('burrowtoken');
    console.log(`[ViewQueueService] Auth token exists: ${!!token}`);
    
    // The API call will return queue with populated video details
    const response = await apiClient.get(`/api/view-queues/play/${queueId}${startVideoId ? `?videoId=${startVideoId}` : ''}`);
    
    console.timeEnd('queue-playback-load-time');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to load queue');
    }
    
    console.log(`Loaded queue with ${response.data.queue.videos.length} videos for playback`);
    
    // Normalize all videos to ensure consistent properties
    const normalizedVideos = response.data.queue.videos.map(video => {
      // Make sure duration is properly captured
      const normalizedVideo = normalizeVideoData(video);
      
      // Debug log for duration
      console.log(`[ViewQueueService] Video ${normalizedVideo.title} - Raw duration: ${video.duration || video.length}, Normalized duration: ${normalizedVideo.duration}`);
      
      return normalizedVideo;
    });
    
    // Find the starting video index if a specific videoId was provided
    let startIndex = 0;
    if (startVideoId && normalizedVideos.length > 0) {
      const foundIndex = normalizedVideos.findIndex(
        video => video.id === startVideoId || video.videoId === startVideoId
      );
      if (foundIndex !== -1) {
        startIndex = foundIndex;
      }
    }
    
    dispatch({
      type: ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_SUCCESS,
      payload: { 
        queue: response.data.queue,
        videos: normalizedVideos,
        startIndex 
      }
    });
    
    return response.data.queue;
  } catch (error) {
    console.error(`Failed to load queue ${queueId} for playback:`, error);
    
    dispatch({
      type: ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to load queue for playback'
    });
    
    return null;
  }
};

// Navigate to next video in queue
export const nextQueueVideo = () => ({
  type: ViewQueueTypes.NEXT_QUEUE_VIDEO
});

// Navigate to previous video in queue  
export const previousQueueVideo = () => ({
  type: ViewQueueTypes.PREVIOUS_QUEUE_VIDEO
});

// Toggle queue autoplay
export const toggleQueueAutoplay = (enabled) => ({
  type: ViewQueueTypes.TOGGLE_QUEUE_AUTOPLAY,
  payload: enabled
});

// Clear queue playback data
export const clearQueuePlayback = () => ({
  type: ViewQueueTypes.CLEAR_QUEUE_PLAYBACK
});

// Helper function to find watchlist queue
const findWatchlistQueue = (userQueues) => {
  if (!userQueues || !Array.isArray(userQueues)) return null;
  return userQueues.find(queue => queue.isWatchlist === true);
};

// Thunk action for adding a video to watchlist
export const addToWatchlist = (videoData) => async (dispatch, getState) => {
  dispatch({ type: ViewQueueTypes.ADD_TO_WATCHLIST_REQUEST });
  
  try {
    // Check if we already have user queues in the state
    let userQueues = getState().viewQueue?.userQueues || [];
    let watchlistQueue = findWatchlistQueue(userQueues);
    
    // If we don't have the watchlist queue yet, fetch user queues
    if (!watchlistQueue) {
      console.log('[WATCHLIST] No watchlist found in current state, fetching queues...');
      const fetchResult = await dispatch(fetchUserQueues());
      userQueues = fetchResult || [];
      watchlistQueue = findWatchlistQueue(userQueues);
    }
    
    // If still no watchlist, something is wrong
    if (!watchlistQueue) {
      console.error('[WATCHLIST] Failed to find watchlist for user');
      throw new Error('Watchlist not found. Please try refreshing the page.');
    }
    
    console.log(`[WATCHLIST] Adding video to watchlist: ${videoData.videoId}`);
    
    // Use existing addVideoToQueue function with watchlist ID
    const result = await dispatch(addVideoToQueue(watchlistQueue.viewQueueID, videoData));
    
    if (result.success) {
      console.log(`[WATCHLIST] Successfully added video to watchlist: ${videoData.videoId}`);
      dispatch({ 
        type: ViewQueueTypes.ADD_TO_WATCHLIST_SUCCESS,
        payload: {
          video: videoData,
          queueId: watchlistQueue.viewQueueID
        }
      });
      
      return { success: true, watchlist: watchlistQueue };
    } else {
      throw new Error(result.error || 'Failed to add video to watchlist');
    }
  } catch (error) {
    console.error('[WATCHLIST-ERROR]', error.message);
    
    dispatch({
      type: ViewQueueTypes.ADD_TO_WATCHLIST_FAILURE,
      payload: error.message || 'Failed to add to watchlist'
    });
    
    return { success: false, error: error.message || 'Failed to add to watchlist' };
  }
};

// Queue playback timestamp updates
export const updateQueueTimestamp = (videoId, timestamp) => async (dispatch, getState) => {
  // Get current queue ID
  const { viewQueue } = getState();
  const { player: { currentQueue, queueVideos } } = viewQueue;
  
  if (!currentQueue || !currentQueue._id) {
    console.error('[ViewQueueService] Cannot update timestamp: No current queue');
    return;
  }
  
  try {
    console.log(`[ViewQueueService] Updating timestamp for video ${videoId} to ${timestamp}s in queue ${currentQueue._id}`);
    
    // Debug: Check the current state of the videos
    const videoToUpdate = queueVideos.find(v => (v.id === videoId || v.videoId === videoId));
    console.log('[ViewQueueService] Current video data:', videoToUpdate);
    console.log('[ViewQueueService] Current timestamp value:', videoToUpdate?.timestamp);
    
    // Update timestamp in local state immediately for UI responsiveness
    dispatch({
      type: ViewQueueTypes.UPDATE_QUEUE_TIMESTAMP,
      payload: {
        videoId,
        timestamp: parseFloat(timestamp), // Ensure it's a number
        queueId: currentQueue._id
      }
    });
    
    try {
      // Try to send update to server if the endpoint exists
      await apiClient.post('/api/view-queues/update-timestamp', {
        queueId: currentQueue._id,
        videoId,
        timestamp: parseFloat(timestamp)
      });
    } catch (serverError) {
      // Silently handle the 404 error - the endpoint doesn't exist yet
      // But we don't want to disrupt the user experience
      console.log('Note: Server-side timestamp saving not yet implemented');
    }
    
    // The local state update was successful even if the server call failed
    return true;
  } catch (error) {
    console.error('Failed to update queue video timestamp:', error);
    return false;
  }
};